/**
 * What the family is saving toward.
 *
 * A goal's "pot" is not a separate pile of money. It is how much of the
 * existing balance counts toward this goal, worked out server-side by
 * `goal_pot` from who is named on it and what their caps are. Nothing is ever
 * moved or reserved -- which is why a goal can be deleted without anybody
 * losing anything, and why two goals can both be nearly full at once.
 *
 * CAPS are the quiet feature that makes shared goals fair. Naming Ravi on a
 * family trip without a cap would put all of his own money behind it. A cap
 * says "Ravi is in, for up to eight dollars".
 *
 * A GOAL ENDS ONE OF TWO WAYS, and a parent always says which. Completed:
 * saved up, and the money spent on it (`redeemGoal`). Missed: it did not
 * happen, and no money moves (`missGoal`). Neither happens on its own -- a
 * goal saved up, or past its date, asks the question on its card and in a
 * notice on Home, and the answer is one confirm away. Five lists: Active,
 * Ready and Past date (both active), Completed and Missed. See family/goals.ts.
 */

import { FamilyMark } from '../components/FamilyMark';
import { useEffect, useState } from 'react';
import {
  archiveGoal,
  FAMILY_POT,
  missGoal,
  isShowing,
  lockGoal,
  refundGoal,
  saveSplits,
  setGoalFunds,
  showGoal,
  moveGoalDate,
  redeemGoal,
  reopenGoal,
  saveGoal,
  GOAL_ICONS,
  GOAL_KINDS,
  type Goal,
  type GoalIcon,
  type GoalKind,
  type Person,
} from '../family/state';
import { Character } from '../avatar/Character';
import { GoalPicture, MoreMenu } from '../brand/GoalPicture';
import { Layer } from '../brand/Layer';
import { ProgressBar } from '../brand/ProgressBar';
import { GOAL_PICTURE_LABELS } from '../brand/goalIcons';
import { amountInputMode, amountPlaceholder, asTyped, currentUnit, dollars, howMuch, looksLikeAmount, normaliseAmount } from '../family/money';
import {
  counts,
  inList,
  localToday,
  readableDate,
  readFocus,
  standing,
  type GoalFilter,
} from '../family/goals';


export function Goals({
  familyId,
  familyKey,
  goals,
  people,
  free = {},
  splits = {},
  busy,
  focus,
  onChanged,
}: {
  familyId: string;
  familyKey: CryptoKey;
  goals: Goal[];
  people: Person[];
  /** What each person, and the family pot, has that is not in a goal. */
  free?: Record<string, number>;
  /** Automatic splits, per child. */
  splits?: Record<string, Record<string, number>>;
  busy: boolean;
  /** Which list to open on, and maybe one goal's question: see `readFocus`. */
  focus?: string | null;
  onChanged: (work: () => Promise<void>) => void;
}) {
  const asked = readFocus(focus);
  const [filter, setFilter] = useState<GoalFilter>(asked.filter);
  const [open, setOpen] = useState<string | null>(asked.open);
  const [adding, setAdding] = useState(false);
  /** The active goal whose form is open in place of its card. */
  const [editing, setEditing] = useState<string | null>(null);
  const [clearing, setClearing] = useState(false);
  const today = localToday();

  // Home can send a parent here more than once without this screen going
  // away (a notice, then another), so a new focus replaces the old one.
  useEffect(() => {
    const next = readFocus(focus);
    setFilter(next.filter);
    setOpen(next.open);
  }, [focus]);

  const count = counts(goals, today);
  const shown = inList(goals, filter, today);
  // A list that has just emptied -- the last ready goal completed -- goes
  // back to all the active ones rather than showing nothing.
  useEffect(() => {
    if ((filter === 'ready' || filter === 'late') && count[filter] === 0) setFilter('active');
  }, [filter, count]);

  const chips: Array<[GoalFilter, string]> = [
    ['active', 'Active'],
    ...(count.ready ? [['ready', 'Ready'] as [GoalFilter, string]] : []),
    ...(count.late ? [['late', 'Past date'] as [GoalFilter, string]] : []),
    ['completed', 'Completed'],
    ['missed', 'Missed'],
  ];

  const faces = (goal: Goal) =>
    goal.people.length ? (
      <span
        className="goal-who"
        role="img"
        aria-label={`For ${goal.people.map((id) => people.find((p) => p.id === id)?.name).filter(Boolean).join(', ')}`}
      >
        {goal.people.map((id) => {
          const who = people.find((p) => p.id === id);
          return who ? <Character key={id} spec={who.avatar} size={26} /> : null;
        })}
      </span>
    ) : null;

  const finished = filter === 'completed' || filter === 'missed';
  const names = (id: string) =>
    id === FAMILY_POT ? 'Family pot' : people.find((p) => p.id === id)?.name || 'Somebody';
  const faceFor = (id: string) => {
    const who = people.find((p) => p.id === id);
    return who ? <Character spec={who.avatar} size={36} /> : <FamilyMark size={36} />;
  };
  const contributorsOf = (goal: Goal) => [
    ...(goal.people.length ? goal.people : people.map((p) => p.id)),
    FAMILY_POT,
  ];

  return (
    <>
      <h2>Goals</h2>

      <div className="goal-filters" role="group" aria-label="Which goals">
        {chips.map(([id, label]) => (
          <button
            key={id}
            type="button"
            className={[
              'goal-filter',
              filter === id ? 'on' : '',
              id === 'ready' ? 'is-ready' : id === 'late' ? 'is-late' : '',
            ].join(' ').trim()}
            aria-pressed={filter === id}
            onClick={() => {
              setFilter(id);
              setOpen(null);
              setClearing(false);
            }}
          >
            {label} <span className="goal-filter-count">{count[id]}</span>
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <p className="footnote">
          {filter === 'completed'
            ? 'Nothing completed yet. When a goal is saved up, completing it spends the money and it moves here.'
            : filter === 'missed'
              ? 'Nothing missed. A goal only moves here when you say it did not happen.'
              : 'No goals yet. Make one, then put money in it from each child — only what they have free.'}
        </p>
      ) : null}

      {!finished
        ? shown.map((goal) => editing === goal.id ? (
            <GoalForm
              key={goal.id}
              initial={goal}
              people={people}
              busy={busy}
              onCancel={() => setEditing(null)}
              onSave={(values) =>
                onChanged(async () => {
                  // The form sends everything but caps; keep those, for the
                  // people still on the goal.
                  const caps = Object.fromEntries(
                    Object.entries(asForm(goal).caps).filter(
                      ([id]) => values.people.length === 0 || values.people.includes(id),
                    ),
                  );
                  await saveGoal(familyId, familyKey, { ...values, caps, id: goal.id }, people);
                  setEditing(null);
                })
              }
            />
          ) : (
            <ActiveGoal
              key={goal.id}
              onEdit={() => setEditing(goal.id)}
              goal={goal}
              today={today}
              faces={faces(goal)}
              names={names}
              faceFor={faceFor}
              contributors={contributorsOf(goal)}
              free={free}
              busy={busy}
              asking={open === goal.id}
              onAsk={(yes) => setOpen(yes ? goal.id : null)}
              onComplete={(showUntil) =>
                onChanged(async () => {
                  await redeemGoal(familyId, familyKey, goal.id, goal.title, showUntil);
                  setOpen(null);
                })
              }
              onRefund={() =>
                onChanged(async () => {
                  await refundGoal(familyId, goal.id);
                  setOpen(null);
                })
              }
              onFunds={(funds, force) =>
                onChanged(async () => {
                  await setGoalFunds(familyId, goal.id, funds, force);
                  setOpen(null);
                })
              }
              onMiss={() =>
                onChanged(async () => {
                  await missGoal(familyId, goal.id);
                  setOpen(null);
                })
              }
              onMove={(due) =>
                onChanged(async () => {
                  await moveGoalDate(familyId, familyKey, goal, due, people);
                  setOpen(null);
                })
              }
              onRemove={() => onChanged(() => archiveGoal(familyId, goal.id))}
              onLock={(locked) => onChanged(() => lockGoal(familyId, goal.id, locked))}
            />
          ))
        : shown.map((goal) => (
            <FinishedGoal
              key={goal.id}
              goal={goal}
              faces={faces(goal)}
              busy={busy}
              onShow={(until) => onChanged(() => showGoal(familyId, goal.id, until))}
              onRename={(title) =>
                onChanged(() => saveGoal(familyId, familyKey, { ...asForm(goal), title, id: goal.id }, people))
              }
              onReopen={(due) => onChanged(() => reopenGoal(familyId, goal.id, due))}
              onRemove={() => onChanged(() => archiveGoal(familyId, goal.id))}
            />
          ))}

      {/* A long history of finished goals is cleared in one go, after asking. */}
      {finished && shown.length > 1 ? (
        clearing ? (
          <div className="danger-box">
            <p>
              Clear all {shown.length} {filter} goals from this list? Nothing
              about the money changes, and the journal keeps every entry.
            </p>
            <div className="actions">
              <button
                type="button"
                className="primary"
                disabled={busy}
                onClick={() =>
                  onChanged(async () => {
                    for (const goal of shown) await archiveGoal(familyId, goal.id);
                    setClearing(false);
                  })
                }
              >
                Clear them
              </button>
              <button type="button" disabled={busy} onClick={() => setClearing(false)}>
                Keep them
              </button>
            </div>
          </div>
        ) : (
          <button type="button" className="text-button" onClick={() => setClearing(true)}>
            Clear all {shown.length} from this list
          </button>
        )
      ) : null}

      {finished ? null : (
        <Splits
          people={people}
          goals={goals}
          splits={splits}
          busy={busy}
          onSave={(next) => onChanged(() => saveSplits(familyId, next))}
        />
      )}

      {finished ? null : adding ? (
        <GoalForm
          people={people}
          busy={busy}
          onCancel={() => setAdding(false)}
          onSave={(values) =>
            onChanged(async () => {
              await saveGoal(familyId, familyKey, values, people);
              setAdding(false);
            })
          }
        />
      ) : (
        <button type="button" className="full add-person" onClick={() => setAdding(true)}>
          + Add a goal
        </button>
      )}
    </>
  );
}

/**
 * One active goal: what is in it, the one question it needs answering, and
 * the three things a parent can do with its money.
 *
 *   put money in   per child and from the family pot, never more than each
 *                  has free -- and when a parent asks for more, the app says
 *                  so and offers what IS free (see FundsPanel)
 *   complete it    at ANY stage, spending exactly what is in it
 *   refund it      everything back to whoever put it in; the goal stays
 *
 * Completing, refunding and missing each ask once before they happen, and the
 * question says what will happen to the money in plain dollars.
 */
function ActiveGoal({
  goal,
  today,
  faces,
  names,
  faceFor,
  contributors,
  free,
  busy,
  asking,
  onAsk,
  onComplete,
  onRefund,
  onFunds,
  onMiss,
  onMove,
  onRemove,
  onLock,
  onEdit,
}: {
  goal: Goal;
  onEdit: () => void;
  today: string;
  faces: React.ReactNode;
  /** Person id (or `family`) to a name. */
  names: (id: string) => string;
  /** Person id (or `family`) to a small face. */
  faceFor: (id: string) => React.ReactNode;
  /** Who may put money in: the people on it, and the family pot. */
  contributors: string[];
  free: Record<string, number>;
  busy: boolean;
  /** Its question is open: set from Home's notice, or by tapping. */
  asking: boolean;
  onAsk: (open: boolean) => void;
  onComplete: (showUntil: string | null) => void;
  onRefund: () => void;
  onFunds: (funds: Record<string, string>, force?: boolean) => void;
  onMiss: () => void;
  onMove: (due: string | null) => void;
  onRemove: () => void;
  onLock: (locked: boolean) => void;
}) {
  const state = standing(goal, today)!;
  /** Which step is showing, when the card is asking something. */
  const [step, setStep] = useState<'complete' | 'refund' | 'miss' | 'date' | 'funds' | null>(null);
  const [due, setDue] = useState(goal.due ?? '');
  /** How long it stays up once complete: a week by default. */
  const [keep, setKeep] = useState<'week' | 'date' | 'always' | 'no'>('week');
  const [keepDate, setKeepDate] = useState(() => inDays(today, 7));
  const showUntil = keep === 'week' ? inDays(today, 7) : keep === 'date' ? keepDate : keep === 'always' ? 'always' : null;
  const shownStep = step ?? (asking ? (state === 'ready' ? 'complete' : state === 'late' ? 'miss' : null) : null);
  const close = () => {
    setStep(null);
    onAsk(false);
  };
  const inIt = Object.entries(goal.funds).filter(([, cents]) => cents > 0);
  const breakdown = inIt.map(([who, cents]) => `${names(who)} ${dollars(cents)}`).join(', ');

  return (
    <div className={`goal goal-${state}${goal.locked ? ' is-locked' : ''}`} id={`goal-${goal.id}`}>
      <div className="goal-head">
        <GoalPicture icon={goal.icon} size={52} />
        <div className="goal-body">
          <p className="goal-title">{goal.title || 'Untitled goal'}</p>
          <p className="goal-money">
            {state === 'ready'
              ? `All ${dollars(goal.pot)} saved`
              : state === 'late'
                ? `Date passed ${readableDate(goal.due!)}`
                : goal.due
                  ? `by ${readableDate(goal.due)}`
                  : goal.locked
                    ? 'Kids can put in, not take out'
                    : 'Kids can take their own out'}
          </p>
        </div>
        <div className="goal-side">
          {state === 'ready' ? (
            <span className="status-pill ready">Ready</span>
          ) : state === 'late' ? (
            <span className="status-pill late">Date passed</span>
          ) : goal.locked ? (
            <span className="status-pill locked">Locked</span>
          ) : null}
          {faces}
        </div>
      </div>

      <GoalBar goal={goal} full={state === 'ready'} />
      {inIt.length ? <p className="goal-funds">{breakdown}</p> : null}

      {state === 'late' && !shownStep ? (
        <div className="goal-callout late">
          <p>
            <strong>Its date was {readableDate(goal.due!)}.</strong> Did it happen?
          </p>
          <div className="actions">
            <button type="button" disabled={busy} onClick={() => setStep('complete')}>
              Yes &mdash; complete it
            </button>
            <button type="button" disabled={busy} onClick={() => setStep('miss')}>
              No, it was missed
            </button>
            <button type="button" disabled={busy} onClick={() => setStep('date')}>
              Give it more time
            </button>
          </div>
        </div>
      ) : null}

      {shownStep === 'funds' ? (
        <FundsPanel
          goal={goal}
          contributors={contributors}
          names={names}
          free={free}
          busy={busy}
          onSave={onFunds}
          onCancel={close}
        />
      ) : null}

      {shownStep === 'complete' ? (
        <Layer onClose={close} busy={busy}>
        <div className="sheet-layer" onClick={(event) => event.target === event.currentTarget && !busy && close()}>
        <div
          className="sheet goal-sheet"
          role="dialog"
          aria-modal="true"
          aria-labelledby={`complete-${goal.id}`}
        >
          <span className="sheet-handle" aria-hidden="true" />
          <GoalPicture icon={goal.icon} size={84} className="ready" />
          <h3 className="sheet-title" id={`complete-${goal.id}`}>
            Complete {goal.title || 'this goal'}?
          </h3>
          <p className="sheet-text">
            {goal.pot > 0 ? (
              <>
                Completing <strong>{goal.title || 'this goal'}</strong> spends the{' '}
                <strong>{dollars(goal.pot)}</strong> in it ({breakdown}) and records it in the journal.
                {goal.pot < goal.target ? ` That is less than its ${dollars(goal.target)} target, which is fine.` : ''}
              </>
            ) : (
              <>
                Nothing is in <strong>{goal.title || 'this goal'}</strong>, so completing it spends
                nothing. It moves to Completed.
              </>
            )}
          </p>
          {inIt.length ? (
            <ul className="sheet-payers">
              {inIt.map(([who, cents]) => (
                <li key={who}>
                  <span className="sheet-payer-face">{faceFor(who)}</span>
                  <span className="sheet-payer-name">{names(who)}</span>
                  <span className="sheet-payer-amount">&minus;{dollars(cents)}</span>
                </li>
              ))}
            </ul>
          ) : null}
          <fieldset className="keep-up">
            <legend>Afterwards, show it as done on the valley and the children&rsquo;s pages:</legend>
            {(
              [
                ['week', 'For a week'],
                ['date', 'Until a date'],
                ['always', 'Until I take it down'],
                ['no', 'Don’t show it'],
              ] as const
            ).map(([id, words]) => (
              <label key={id}>
                <input type="radio" name={`keep-${goal.id}`} checked={keep === id} onChange={() => setKeep(id)} />
                <span>{words}</span>
              </label>
            ))}
            {keep === 'date' ? (
              <input type="date" value={keepDate} min={today} onChange={(event) => setKeepDate(event.target.value)} aria-label="Show it until" />
            ) : null}
          </fieldset>
          <div className="sheet-actions">
            <button aria-busy={busy} type="button" className="primary full" disabled={busy || (keep === 'date' && !keepDate)} onClick={() => onComplete(showUntil)}>
              {busy ? 'Completing…' : goal.pot > 0 ? `Complete it — spend ${dollars(goal.pot)}` : 'Complete it'}
            </button>
            <button type="button" className="text-button sheet-cancel" disabled={busy} onClick={close}>
              Not yet
            </button>
          </div>
        </div>
        </div>
        </Layer>
      ) : null}

      {shownStep === 'refund' ? (
        <div className="goal-callout">
          <p>
            Give back the <strong>{dollars(goal.pot)}</strong> in it? {breakdown} &mdash; each gets
            back what they put in. The goal stays, empty.
          </p>
          <div className="actions">
            <button aria-busy={busy} type="button" className="primary" disabled={busy} onClick={onRefund}>
              {busy ? 'Saving…' : 'Give it back'}
            </button>
            <button type="button" disabled={busy} onClick={close}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {shownStep === 'miss' ? (
        <div className="goal-callout late">
          <p>
            Mark <strong>{goal.title || 'this goal'}</strong> as missed?
            {goal.pot > 0 ? ` The ${dollars(goal.pot)} in it goes back to whoever put it in.` : ' No money moves.'}{' '}
            It goes to the Missed list, and you can try it again from there.
          </p>
          <div className="actions">
            <button aria-busy={busy} type="button" className="primary" disabled={busy} onClick={onMiss}>
              {busy ? 'Saving…' : 'Yes, it was missed'}
            </button>
            {state === 'late' ? (
              <button type="button" disabled={busy} onClick={() => setStep('date')}>
                Give it more time
              </button>
            ) : null}
            <button type="button" disabled={busy} onClick={close}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {shownStep === 'date' ? (
        <div className="goal-callout">
          <label className="field">
            <span>New date (leave empty for no date)</span>
            <input type="date" value={due} min={today} onChange={(event) => setDue(event.target.value)} />
          </label>
          <div className="actions">
            <button aria-busy={busy}
              type="button"
              className="primary"
              disabled={busy || (due !== '' && due < today)}
              onClick={() => onMove(due || null)}
            >
              {busy ? 'Saving…' : 'Save the date'}
            </button>
            <button type="button" disabled={busy} onClick={close}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {/* Still there behind the complete-it sheet, so focus can come back
          to the button that opened it when the sheet closes. */}
      {!shownStep || shownStep === 'complete' ? (
        <div className="goal-actions" aria-hidden={shownStep === 'complete' ? true : undefined}>
          {state === 'ready' ? (
            <button type="button" className="sun" disabled={busy} onClick={() => setStep('complete')}>
              Complete it
            </button>
          ) : (
            <button
              type="button"
              className={state === 'late' ? '' : goal.locked ? 'ghost-strong' : 'primary'}
              disabled={busy}
              onClick={() => setStep('funds')}
            >
              {goal.pot > 0 ? 'Change money in it' : 'Put money in'}
            </button>
          )}
          <MoreMenu label={`More for ${goal.title || 'this goal'}`}>
            <button type="button" disabled={busy} onClick={onEdit}>
              Edit
            </button>
            {/* The lock is the switch. Goals start locked: children can put
                money in, and a parent taps the lock to let them take it out. */}
            <button
              type="button"
              className={goal.locked ? 'goal-lock-toggle locked' : 'goal-lock-toggle'}
              aria-pressed={goal.locked}
              disabled={busy}
              title={
                goal.locked
                  ? 'Kids can put money in but not take it out. Tap to unlock.'
                  : 'Kids can take their own money out. Tap to lock.'
              }
              onClick={() => onLock(!goal.locked)}
            >
              {goal.locked ? 'Locked for kids' : 'Unlocked for kids'}
              <small>{goal.locked ? 'Tap to let them take money out' : 'Tap to lock it'}</small>
            </button>
            {state === 'ready' && goal.pot > 0 ? (
              <button type="button" disabled={busy} onClick={() => setStep('funds')}>
                Change money in it
              </button>
            ) : null}
            {state === 'saving' ? (
              <button type="button" disabled={busy} onClick={() => setStep('complete')}>
                Complete it
              </button>
            ) : null}
            {goal.pot > 0 ? (
              <button type="button" disabled={busy} onClick={() => setStep('refund')}>
                Give it back
              </button>
            ) : null}
            {state !== 'late' ? (
              <button type="button" disabled={busy} onClick={() => setStep('date')}>
                {goal.due ? 'Change date' : 'Add a date'}
              </button>
            ) : null}
            <button type="button" disabled={busy} onClick={() => setStep('miss')}>
              Mark as missed
            </button>
            <button type="button" className="risky" disabled={busy} onClick={onRemove}>
              Remove
            </button>
          </MoreMenu>
        </div>
      ) : null}
    </div>
  );
}

/**
 * How much each contributor has in a goal, edited as totals.
 *
 * NOBODY CAN PUT IN MORE THAN THEY HAVE FREE: a child who has not earned five
 * dollars cannot have five dollars in a goal. Each row says what is free. If a
 * parent asks for more anyway, saving does not just fail -- it says who is
 * short and by how much, and offers to put in what they DO have instead, in
 * one tap. The server holds the same line (`_funds`), so this is the friendly
 * half of a rule that cannot be broken.
 */
function FundsPanel({
  goal,
  contributors,
  names,
  free,
  busy,
  onSave,
  onCancel,
}: {
  goal: Goal;
  contributors: string[];
  names: (id: string) => string;
  free: Record<string, number>;
  busy: boolean;
  onSave: (funds: Record<string, string>, force?: boolean) => void;
  onCancel: () => void;
}) {
  const [typed, setTyped] = useState<Record<string, string>>(() =>
    Object.fromEntries(contributors.map((who) => [who, goal.funds[who] ? asTyped(goal.funds[who]!) : ''])),
  );
  const [short, setShort] = useState<Array<{ who: string; asked: number; most: number }>>([]);

  const cents = (value: string) => Math.round(parseFloat(normaliseAmount(value) || '0') * 100);
  const valid = contributors.every((who) => (typed[who] ?? '') === '' || looksLikeAmount(typed[who]!));
  /** The most a contributor can have in this goal: what is in it, plus what is free. */
  const most = (who: string) => (goal.funds[who] ?? 0) + Math.max(0, free[who] ?? 0);

  function wanted(): Record<string, number> {
    return Object.fromEntries(contributors.map((who) => [who, cents(typed[who] ?? '')]));
  }

  function send(amounts: Record<string, number>, force = false): void {
    const changed = contributors.filter((who) => (amounts[who] ?? 0) !== (goal.funds[who] ?? 0));
    onSave(Object.fromEntries(changed.map((who) => [who, asTyped(amounts[who] ?? 0)])), force);
  }

  function save(): void {
    const amounts = wanted();
    const over = contributors
      .filter((who) => (amounts[who] ?? 0) > most(who))
      .map((who) => ({ who, asked: amounts[who] ?? 0, most: most(who) }));
    if (over.length) {
      setShort(over);
      return;
    }
    send(amounts);
  }

  return (
    <div className="goal-callout funds-panel">
      <p>
        <strong>Money in {goal.title || 'this goal'}</strong> &mdash; set aside, not spent. It stays theirs
        until you complete the goal.
      </p>
      {contributors.map((who) => (
        <label className="field funds-row" key={who}>
          <span>{names(who)}</span>
          <input
            value={typed[who] ?? ''}
            onChange={(event) => {
              setTyped((current) => ({ ...current, [who]: event.target.value }));
              setShort([]);
            }}
            inputMode={amountInputMode()}
            autoComplete="off"
            placeholder={amountPlaceholder()}
            aria-label={`Amount from ${names(who)}`}
          />
          <small>
            {dollars(Math.max(0, free[who] ?? 0))} free
            {(free[who] ?? 0) < 0 ? ` (${dollars(-(free[who] ?? 0))} short already)` : ''}
            {most(who) > 0 ? (
              <button
                type="button"
                className="text-button"
                onClick={() => {
                  setTyped((current) => ({ ...current, [who]: asTyped(most(who)) }));
                  setShort([]);
                }}
              >
                Use all of it
              </button>
            ) : null}
          </small>
        </label>
      ))}

      {short.length ? (
        <div className="danger-box" role="alert">
          {short.map(({ who, asked, most: can }) => (
            <p key={who}>
              <strong>{names(who)}</strong> only has {dollars(can)} for this &mdash; you asked for{' '}
              {dollars(asked)}.
            </p>
          ))}
          <p>
            Put in what they have instead? Or, since you are the parent, put in the full amount
            anyway and {short.length === 1 ? names(short[0]!.who) : 'they'} will owe the rest
            {short.length === 1 ? ` (${dollars(short[0]!.most - short[0]!.asked)})` : ''}.
          </p>
          <div className="actions">
            <button
              type="button"
              className="primary"
              disabled={busy}
              onClick={() => {
                const amounts = wanted();
                for (const { who, most: can } of short) amounts[who] = can;
                setShort([]);
                send(amounts);
              }}
            >
              {short.length === 1 ? `Put in ${dollars(short[0]!.most)}` : 'Put in what they have'}
            </button>
            {short.every(({ who }) => who !== FAMILY_POT) ? (
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  const amounts = wanted();
                  setShort([]);
                  send(amounts, true);
                }}
              >
                Put it all in anyway
              </button>
            ) : null}
            <button type="button" disabled={busy} onClick={() => setShort([])}>
              Change the amounts
            </button>
          </div>
        </div>
      ) : null}

      <div className="actions">
        <button aria-busy={busy} type="button" className="primary" disabled={busy || !valid || short.length > 0} onClick={save}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button type="button" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/** A completed or missed goal: what it was, when, and a way to move on. */
function FinishedGoal({
  goal,
  faces,
  busy,
  onReopen,
  onRemove,
  onShow,
  onRename,
}: {
  goal: Goal;
  faces: React.ReactNode;
  busy: boolean;
  /** A finished goal keeps its money and dates; only its name can change. */
  onRename: (title: string) => void;
  onReopen: (due: string | null) => void;
  onRemove: () => void;
  onShow: (until: string | null) => void;
}) {
  const [reopening, setReopening] = useState(false);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [keeping, setKeeping] = useState(false);
  const [keepDate, setKeepDate] = useState(() => inDays(localToday(), 7));
  const [due, setDue] = useState('');
  const today = localToday();
  const missed = goal.status === 'missed';
  const when = goal.closed ? ` ${readableDate(goal.closed)}` : '';

  return (
    <div className={missed ? 'goal goal-missed' : 'goal goal-completed'}>
      {!missed && isShowing(goal, today) ? <Petals /> : null}
      <div className="goal-head">
        <GoalPicture icon={goal.icon} size={52} className={missed ? 'missed' : 'done'} />
        <div className="goal-body">
          {renaming === null ? (
            <div className="goal-title-row">
              <p className="goal-title">{goal.title || 'Untitled goal'}</p>
              <button
                type="button"
                className="text-button goal-edit"
                disabled={busy}
                onClick={() => setRenaming(goal.title)}
              >
                Rename
              </button>
            </div>
          ) : (
            <form
              className="goal-rename"
              onSubmit={(event) => {
                event.preventDefault();
                if (!renaming.trim()) return;
                onRename(renaming.trim());
                setRenaming(null);
              }}
            >
              <input
                value={renaming}
                onChange={(event) => setRenaming(event.target.value)}
                maxLength={80}
                aria-label="Goal name"
                autoFocus
              />
              <button type="submit" className="primary" disabled={busy || !renaming.trim()}>
                Save
              </button>
              <button type="button" disabled={busy} onClick={() => setRenaming(null)}>
                Cancel
              </button>
            </form>
          )}
          {faces}
          <p className="goal-money">
            {missed
              ? `Missed${when} · ${dollars(Math.min(goal.pot, goal.target))} of ${dollars(goal.target)} saved`
              : `Completed${when} · spent ${dollars(goal.spent ?? 0)}`}
          </p>
          {!missed ? (
            <p className="goal-funds">
              {isShowing(goal, today)
                ? goal.showUntil === 'always'
                  ? 'Up on the valley until you take it down'
                  : `Up on the valley until ${readableDate(goal.showUntil!)}`
                : 'Not shown on the valley'}
            </p>
          ) : null}
        </div>
      </div>

      {keeping ? (
        <div className="goal-callout">
          <label className="field">
            <span>Show it as done until</span>
            <input type="date" value={keepDate} min={today} onChange={(event) => setKeepDate(event.target.value)} />
          </label>
          <div className="actions">
            <button type="button" className="primary" disabled={busy || !keepDate} onClick={() => { onShow(keepDate); setKeeping(false); }}>
              Until then
            </button>
            <button type="button" disabled={busy} onClick={() => { onShow('always'); setKeeping(false); }}>
              Until I take it down
            </button>
            <button type="button" disabled={busy} onClick={() => setKeeping(false)}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {reopening ? (
        <div className="goal-callout">
          <label className="field">
            <span>A new date (optional)</span>
            <input type="date" value={due} min={today} onChange={(event) => setDue(event.target.value)} />
          </label>
          <div className="actions">
            <button aria-busy={busy} type="button" className="primary" disabled={busy} onClick={() => onReopen(due || null)}>
              {busy ? 'Saving…' : 'Try it again'}
            </button>
            <button type="button" disabled={busy} onClick={() => setReopening(false)}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="secret-actions">
          {missed ? (
            <button type="button" className="text-button" disabled={busy} onClick={() => setReopening(true)}>
              Try again
            </button>
          ) : null}
          {!missed && isShowing(goal, today) ? (
            <button type="button" className="text-button" disabled={busy} onClick={() => onShow(null)}>
              Take it down
            </button>
          ) : null}
          {!missed ? (
            <button type="button" className="text-button" disabled={busy} onClick={() => setKeeping(!keeping)}>
              {isShowing(goal, today) ? 'Change how long' : 'Put it back up'}
            </button>
          ) : null}
          <button type="button" className="text-button" disabled={busy} onClick={onRemove}>
            Clear from list
          </button>
        </div>
      )}
    </div>
  );
}

/** A date `n` days after `iso`, as YYYY-MM-DD. Noon, so it never slips a day. */
function inDays(iso: string, n: number): string {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + n);
  return localToday(d);
}

/**
 * Automatic saving: per child, a percentage of every deposit into goals.
 *
 * The save/spend/give jar every allowance app has, made of this family's own
 * goals. What is not split stays free to spend. The server applies it to
 * chores and allowance alike, rounds whole units down, and never takes a
 * child who owes below zero to do it (services/api/valley/service.py
 * `_split_ops`).
 */
function Splits({
  people,
  goals,
  splits,
  busy,
  onSave,
}: {
  people: Person[];
  goals: Goal[];
  splits: Record<string, Record<string, number>>;
  busy: boolean;
  onSave: (splits: Record<string, Record<string, number>>) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Record<string, Record<string, string>>>({});
  const active = goals.filter((g) => g.status === 'active');
  const canGo = (who: string) => active.filter((g) => g.people.length === 0 || g.people.includes(who));
  const title = (id: string) => active.find((g) => g.id === id)?.title || 'a goal';
  const kids = people.filter((p) => canGo(p.id).length > 0);

  const summary = kids
    .map((p) => {
      const rules = Object.entries(splits[p.id] ?? {}).filter(([id]) => active.some((g) => g.id === id));
      return rules.length
        ? `${p.name || 'Somebody'}: ${rules.map(([id, pct]) => `${pct}% to ${title(id)}`).join(', ')}`
        : '';
    })
    .filter(Boolean);

  const total = (who: string) =>
    Object.values(draft[who] ?? {}).reduce((sum, v) => sum + (Number(v) || 0), 0);
  const valid = kids.every(
    (p) =>
      total(p.id) <= 100 &&
      Object.values(draft[p.id] ?? {}).every((v) => v === '' || (/^\d{1,3}$/.test(v) && Number(v) <= 100)),
  );

  if (!kids.length) return null;
  return (
    <section className="splits">
      <p className="field-label">Automatic saving</p>
      {!editing ? (
        <>
          <p className="footnote">
            {summary.length
              ? summary.join(' · ')
              : 'Put part of every allowance and chore straight into a goal — save, spend, give.'}
          </p>
          <button
            type="button"
            className="text-button"
            onClick={() => {
              setDraft(
                Object.fromEntries(
                  kids.map((p) => [
                    p.id,
                    Object.fromEntries(canGo(p.id).map((g) => [g.id, String(splits[p.id]?.[g.id] ?? '')])),
                  ]),
                ),
              );
              setEditing(true);
            }}
          >
            {summary.length ? 'Change it' : 'Set it up'}
          </button>
        </>
      ) : (
        <div className="record">
          {kids.map((p) => (
            <div key={p.id} className="split-person">
              <p className="split-who">
                {p.name || 'Somebody'}
                <span className={total(p.id) > 100 ? 'split-rest bad' : 'split-rest'}>
                  {total(p.id) > 100 ? 'More than 100%' : `${100 - total(p.id)}% stays free to spend`}
                </span>
              </p>
              {canGo(p.id).map((g) => (
                <label key={g.id} className="split-row">
                  <span>{g.title || 'Untitled goal'}</span>
                  <input
                    value={draft[p.id]?.[g.id] ?? ''}
                    onChange={(event) =>
                      setDraft((all) => ({ ...all, [p.id]: { ...all[p.id], [g.id]: event.target.value.trim() } }))
                    }
                    inputMode="numeric"
                    placeholder="0"
                    aria-label={`Percent of ${p.name || 'their'} deposits into ${g.title || 'this goal'}`}
                  />
                  <span aria-hidden="true">%</span>
                </label>
              ))}
            </div>
          ))}
          <p className="footnote">
            Taken from every allowance payment and every chore as it arrives. Whole marbles stay
            whole; a child who owes gets paid back first.
          </p>
          <div className="actions">
            <button
              type="button"
              className="primary"
              disabled={busy || !valid}
              onClick={() => {
                const out: Record<string, Record<string, number>> = {};
                for (const [who, rules] of Object.entries(draft)) {
                  const kept = Object.fromEntries(
                    Object.entries(rules).filter(([, v]) => Number(v) > 0).map(([id, v]) => [id, Number(v)]),
                  );
                  if (Object.keys(kept).length) out[who] = kept;
                }
                onSave(out);
                setEditing(false);
              }}
            >
              Save
            </button>
            <button type="button" disabled={busy} onClick={() => setEditing(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

/**
 * The Living Valley progress bar: tall, with the amounts written inside it
 * ("$7" in the fill, "of $60" at the end) so the number and the bar are one
 * thing instead of a figure above a thin line.
 */
function GoalBar({ goal, full }: { goal: Goal; full: boolean }) {
  return (
    <ProgressBar
      label={goal.title || 'Goal'}
      value={goal.pot}
      target={goal.target}
      full={full}
      now={full ? `${dollars(goal.pot)} saved` : dollars(goal.pot)}
      of={full ? `${percent(goal.pot, goal.target)}%` : `of ${dollars(goal.target)}`}
    />
  );
}

/** Petals drifting down over a goal that is done and still up on the valley. */
function Petals() {
  return (
    <span className="petals" aria-hidden="true">
      {Array.from({ length: 9 }, (_, i) => (
        <i key={i} style={{ '--i': i } as React.CSSProperties} />
      ))}
    </span>
  );
}

/**
 * Progress, clamped.
 *
 * A pot can exceed a target, and a bar wider than its track looks like a bug
 * rather than good news. This is presentation arithmetic on two numbers the
 * server already worked out -- no money is being computed here.
 */
function percent(pot: number, target: number): number {
  if (target <= 0) return 100;
  return Math.max(0, Math.min(100, Math.round((pot / target) * 100)));
}

function GoalForm({
  initial,
  people,
  busy,
  onSave,
  onCancel,
}: {
  /** Editing this goal rather than making a new one. */
  initial?: Goal;
  people: Person[];
  busy: boolean;
  onSave: (values: {
    title: string;
    target: string;
    kind: GoalKind;
    icon: GoalIcon;
    due?: string;
    people: string[];
    caps?: Record<string, string>;
  }) => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState(initial?.title ?? '');
  const [target, setTarget] = useState(initial ? asTyped(initial.target) : '');
  const [kind, setKind] = useState<GoalKind>(initial?.kind ?? 'small');
  const [icon, setIcon] = useState<GoalIcon>(initial?.icon ?? 'star');
  const [due, setDue] = useState(initial?.due ?? '');
  const [chosen, setChosen] = useState<string[]>(initial?.people ?? []);

  const ready = title.trim() !== '' && looksLikeAmount(target);
  /**
   * Why Save is off, in the order a parent fills the form in.
   *
   * A greyed-out button with no reason beside it is indistinguishable from a
   * broken one, and this form had more than one way to earn it.
   */
  const blocker =
    title.trim() === ''
      ? 'Give the goal a name.'
      : !looksLikeAmount(target)
        ? target.trim() === ''
          ? 'Set an amount to save towards.'
          : 'That amount needs to be dollars and cents, like 1500 or 1500.00.'
        : '';

  function toggle(id: string): void {
    setChosen((current) =>
      current.includes(id) ? current.filter((one) => one !== id) : [...current, id],
    );
  }

  return (
    <div className="record">
      <label className="field">
        <span>What is it?</span>
        <input
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          maxLength={80}
          autoComplete="off"
          placeholder="Camping trip"
        />
        <small>Encrypted before it leaves this device.</small>
      </label>

      <label className="field">
        <span>{currentUnit().type === 'currency' ? 'How much does it cost?' : howMuch()}</span>
        <input
          value={target}
          onChange={(event) => setTarget(event.target.value)}
          inputMode={amountInputMode()}
          autoComplete="off"
          placeholder={amountPlaceholder()}
        />
      </label>

      <p className="field-label">Picture</p>
      <div className="chips">
        {GOAL_ICONS.map((one) => (
          <button
            type="button"
            key={one}
            className={icon === one ? 'chip picture-chip on' : 'chip picture-chip'}
            onClick={() => setIcon(one)}
            aria-label={GOAL_PICTURE_LABELS[one]}
            aria-pressed={icon === one}
          >
            <GoalPicture icon={one} size={52} />
          </button>
        ))}
      </div>

      <p className="field-label">Size</p>
      <div className="segmented">
        {GOAL_KINDS.map((one) => (
          <button
            type="button"
            key={one}
            className={kind === one ? 'on' : ''}
            onClick={() => setKind(one)}
          >
            {one === 'small' ? 'Something small' : 'Something big'}
          </button>
        ))}
      </div>

      <label className="field">
        <span>By when? (optional)</span>
        <input
          type="date"
          value={due}
          onChange={(event) => setDue(event.target.value)}
        />
      </label>

      {people.length ? (
        <>
          <p className="field-label">Who is it for?</p>
          <div className="chips">
            {people.map((person) => (
              <button
                type="button"
                key={person.id}
                className={chosen.includes(person.id) ? 'chip on' : 'chip'}
                onClick={() => toggle(person.id)}
              >
                {person.name || 'Unnamed'}
              </button>
            ))}
          </div>
          <p className="footnote">
            Who can put money in it. Choose nobody and anybody can. The family
            pot can always put money in too.
            {initial
              ? ' Taking somebody off gives back what they have put in.'
              : ''}
          </p>
        </>
      ) : null}

      {blocker ? <p className="footnote">{blocker}</p> : null}

      <div className="actions">
        <button aria-busy={busy}
          className="primary"
          type="button"
          disabled={busy || !ready}
          onClick={() =>
            onSave({
              title: title.trim(),
              target: normaliseAmount(target),
              kind,
              icon,
              ...(due ? { due } : {}),
              people: chosen,
            })
          }
        >
          {busy ? 'Saving…' : initial ? 'Save changes' : 'Save it'}
        </button>
        <button type="button" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/**
 * A goal as the form would have it, so an edit sends everything back --
 * caps included, which the form does not show and a save must not drop.
 */
function asForm(goal: Goal) {
  return {
    title: goal.title,
    target: (goal.target / 100).toFixed(2),
    kind: goal.kind,
    icon: goal.icon,
    ...(goal.due ? { due: goal.due } : {}),
    people: goal.people,
    caps: Object.fromEntries(Object.entries(goal.caps).map(([id, cents]) => [id, (cents / 100).toFixed(2)])),
  };
}
